import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import { getComicsByCategory, getCategoryBySlug, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = getCategoryBySlug(slug || '');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-error-warning-line text-foreground-300 text-6xl w-16 h-16 flex items-center justify-center mx-auto mb-4"></i>
            <h2 className="text-xl font-heading text-foreground-900 mb-2">分类未找到</h2>
            <p className="text-sm text-foreground-500 mb-4">该分类不存在或已被移除</p>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const categoryComics = getComicsByCategory(slug || '');
  const sortedComics = [...categoryComics].sort((a, b) => {
    if (sortBy === 'latest') return new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime();
    if (sortBy === 'popular') return b.views - a.views;
    return b.rating - a.rating;
  });

  const catJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 韩漫网`,
    description: `${category.description}，韩漫网为您精选${category.count}部${category.name}韩漫，支持免费在线阅读。`,
    url: `https://www.hanmanwang.com/category/${slug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫网',
      url: 'https://www.hanmanwang.com',
    },
  };

  const sortOptions: { value: 'latest' | 'popular' | 'rating'; label: string }[] = [
    { value: 'latest', label: '最新' },
    { value: 'popular', label: '最热' },
    { value: 'rating', label: '评分' },
  ];

  return (
    <>
      <SEOMeta
        title={`${category.name}韩漫 - 韩漫网`}
        description={`${category.description}，韩漫网为您精选${category.count}部${category.name}韩漫，支持免费在线阅读。每日更新${category.name}题材最新韩漫。`}
        keywords={`${category.name}韩漫,${category.name}漫画,韩漫${category.name},韩漫网${category.name}`}
        canonicalUrl={`https://www.hanmanwang.com/category/${slug}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(catJsonLd) }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="bg-background-100 border-b border-background-200">
            <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: category.name },
                ]}
              />
              <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mt-4">{category.name}韩漫</h1>
              <p className="mt-2 text-sm text-foreground-600">{category.description}</p>
            </div>
          </div>

          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="lg:w-56 flex-shrink-0">
                <div className="lg:sticky lg:top-24">
                  <h3 className="font-semibold text-sm text-foreground-900 mb-3">全部分类</h3>
                  <nav className="space-y-1">
                    {categories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                          cat.slug === slug
                            ? 'bg-primary-50 text-primary-500 font-medium'
                            : 'text-foreground-600 hover:text-primary-500 hover:bg-background-100'
                        }`}
                      >
                        {cat.name}
                        <span className="ml-1.5 text-xs text-foreground-400">({cat.count})</span>
                      </Link>
                    ))}
                  </nav>
                </div>
              </aside>

              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-foreground-600">
                    共 <strong className="text-foreground-900">{categoryComics.length}</strong> 部作品
                  </p>
                  <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                    {sortOptions.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => setSortBy(opt.value)}
                        className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                          sortBy === opt.value
                            ? 'bg-background-50 text-primary-500 shadow-sm'
                            : 'text-foreground-500 hover:text-foreground-700'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {sortedComics.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                    {sortedComics.map((comic) => (
                      <ComicCard key={comic.id} comic={comic} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <i className="ri-book-line text-foreground-300 text-5xl w-12 h-12 flex items-center justify-center mx-auto mb-4"></i>
                    <p className="text-foreground-500">该分类暂无漫画</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}